import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@/lib/icons';
import { safeReturnPath } from '@/lib/safe-return-path';
import { Button } from '@/components/ui/button';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';

export function DataPolicyActions() {
  const { t, acceptPolicy, hasAcceptedPolicy } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || 'fr';
  const [searchParams] = useSearchParams();
  // Back to the page that asked for the policy.
  const returnPath = safeReturnPath(searchParams.get('next'), currentLang);

  const handleAccept = () => {
    acceptPolicy();
    localStorage.setItem('dataPolicyAccepted', 'true');
    localStorage.setItem('dataPolicyAcceptedDate', new Date().toISOString());
    toast({ title: t.dataPolicyAcceptedToast, description: t.dataPolicyAcceptedToastDesc });
    navigate(returnPath);
  };

  const handleDecline = () => {
    localStorage.setItem('dataPolicyAccepted', 'false');
    localStorage.setItem('dataPolicyDeclinedDate', new Date().toISOString());
    toast({ title: t.dataPolicyDeclinedToast, description: t.dataPolicyDeclinedToastDesc, variant: 'destructive' });
    navigate(`/${currentLang}`);
  };

  const handleVisitUmbaji = () => {
    const linkLang = t.dataPolicyMoreInfo.includes('/fr/') ? 'fr' : 'en';
    window.open(`https://www.umbaji.org/lang/${linkLang}/`, '_blank');
  };

  if (hasAcceptedPolicy) {
    return (
      <section className="pt-7">
        <div className="flex items-center justify-between gap-4 rounded-2xl border border-border/40 bg-background/60 px-6 py-5">
          <div>
            <p className="text-sm font-medium text-foreground">{t.dataPolicyAlreadyAccepted}</p>
            <p className="mt-0.5 text-xs text-muted-foreground/70">{t.dataPolicyAlreadyAcceptedDesc}</p>
          </div>
          <Button variant="outline" onClick={() => navigate(`/${currentLang}`)} className="shrink-0 rounded-full border-border/40 px-5 text-xs font-semibold">
            {t.back}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-6 pt-7">
      <div className="flex items-center justify-between rounded-2xl border border-border/40 bg-background/60 px-6 py-5">
        <span className="text-sm text-muted-foreground/75">{t.dataPolicyMoreInfo.split('www.umbaji.org')[0]}</span>
        <Button variant="outline" onClick={handleVisitUmbaji} className="shrink-0 rounded-full border-border/40 px-4 text-xs font-semibold">
          <Icon name="externalLink" className="mr-2 h-3.5 w-3.5" />
          www.umbaji.org
        </Button>
      </div>

      <div className="flex gap-3">
        <Button
          onClick={handleAccept}
          className="flex-1 h-11 rounded-full border border-primary/20 bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-none transition-colors hover:bg-primary/90 disabled:border-border/30 disabled:bg-background/40 disabled:text-muted-foreground"
        >
          {t.dataPolicyAccept}
        </Button>
        <Button
          onClick={handleDecline}
          variant="outline"
          className="flex-1 h-11 rounded-full border-border/40 px-5 text-sm font-semibold transition-colors hover:bg-muted/20 hover:text-foreground"
        >
          {t.dataPolicyDecline}
        </Button>
      </div>
    </section>
  );
}
